import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

const PopupContext = createContext(null);

const INITIAL_STATE = {
  open: false,
  type: "success", // success | error | info
  title: "",
  message: "",
  actionLabel: "",
  actionHref: "",
  autoHide: true,
  autoHideMs: 2200,
};

function GlobalPopup({
  open,
  type = "success",
  title = "",
  message = "",
  actionLabel = "",
  actionHref = "",
  autoHide = true,
  autoHideMs = 2200,
  onClose,
}) {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open || !autoHide) return;

    timerRef.current = setTimeout(() => {
      onClose?.();
      if (actionHref) navigate(actionHref);
    }, autoHideMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [open, autoHide, autoHideMs, onClose, actionHref, navigate]);

  if (!open) return null;

  const isError = type === "error";
  const isInfo = type === "info";

  const iconBg = isError
    ? "rgba(220, 38, 38, 0.10)"
    : isInfo
    ? "rgba(59, 130, 246, 0.10)"
    : "rgba(16, 185, 129, 0.10)";

  const iconColor = isError ? "#dc2626" : isInfo ? "#2563eb" : "#10b981";
  const borderColor = isError
    ? "1px solid rgba(220, 38, 38, 0.22)"
    : isInfo
    ? "1px solid rgba(59, 130, 246, 0.22)"
    : "1px solid rgba(16, 185, 129, 0.22)";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(15, 23, 42, 0.42)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          borderRadius: 20,
          padding: 22,
          boxShadow: "0 24px 60px rgba(0,0,0,0.20)",
          border: borderColor,
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 14,
            background: iconBg,
            color: iconColor,
            fontSize: 24,
            fontWeight: 900,
          }}
        >
          {isError ? "!" : isInfo ? "…" : "✓"}
        </div>

        <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>
          {title || (isError ? "Action needed" : isInfo ? "Please wait" : "Success")}
        </div>

        <div style={{ fontSize: 14, lineHeight: 1.5, color: "#475569" }}>
          {message}
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 18,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "1px solid rgba(15,23,42,0.10)",
              background: "#fff",
              color: "#0f172a",
              padding: "10px 14px",
              borderRadius: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Close
          </button>

          {actionHref ? (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                navigate(actionHref);
              }}
              style={{
                border: "none",
                background: "#111827",
                color: "#fff",
                padding: "10px 14px",
                borderRadius: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {actionLabel || "Open"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function PopupProvider({ children }) {
  const [popup, setPopup] = useState(INITIAL_STATE);

  const showPopup = useCallback((config) => {
    setPopup({
      ...INITIAL_STATE,
      ...config,
      open: true,
    });
  }, []);

  const closePopup = useCallback(() => {
    setPopup((prev) => ({ ...prev, open: false }));
  }, []);

  const value = useMemo(
    () => ({
      showPopup,
      closePopup,
    }),
    [showPopup, closePopup]
  );

  return (
    <PopupContext.Provider value={value}>
      {children}
      <GlobalPopup {...popup} onClose={closePopup} />
    </PopupContext.Provider>
  );
}

export function usePopup() {
  const ctx = useContext(PopupContext);
  if (!ctx) {
    throw new Error("usePopup must be used inside PopupProvider");
  }
  return ctx;
}